import {
  isDateOnlyString,
  isDateTimeLocalString,
  parseHstDateTimeValue,
  parseHstDateValue,
  toHstDisplayDate,
  toHstDisplayDateTime,
} from '../utils/hstDateInput';

type JsonDateLike = { $date?: unknown } | { date?: unknown };

/**
 * Attempt to coerce heterogeneous date inputs (ISO strings, Date objects, Mongo-style objects, etc.)
 * into a valid Date instance. Returns null when parsing fails.
 */
export function parseDateInput(value: unknown): Date | null {
  if (!value && value !== 0) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (isDateOnlyString(trimmed)) {
      return toHstDisplayDate(parseHstDateValue(trimmed));
    }

    if (isDateTimeLocalString(trimmed)) {
      return toHstDisplayDateTime(parseHstDateTimeValue(trimmed));
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object' && value !== null) {
    const candidate = value as JsonDateLike;
    const raw = ('$date' in candidate && candidate.$date !== undefined)
      ? candidate.$date
      : ('date' in candidate ? candidate.date : undefined);
    if (raw) {
      return parseDateInput(raw);
    }
    if (
      'toDate' in (value as Record<string, unknown>)
      && typeof (value as { toDate?: unknown }).toDate === 'function'
    ) {
      try {
        const converted = (value as { toDate: () => unknown }).toDate();
        return parseDateInput(converted);
      } catch {
        return null;
      }
    }
  }

  return null;
}
